import { PlusIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { AddExpenseModal } from '../../components/inventory-costs/AddExpenseModal';
import { CategoryBreakdownPie } from '../../components/inventory-costs/CategoryBreakdownPie';
import { KpiCards } from '../../components/inventory-costs/KpiCards';
import { MonthlySpendBar } from '../../components/inventory-costs/MonthlySpendBar';
import { VehicleCostTable } from '../../components/inventory-costs/VehicleCostTable';
import { InventoryCostsSummary, inventoryCostsApi, Period } from '../../services/inventoryCostsApi';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'ytd', label: 'YTD' },
  { value: 'year_end', label: 'Year End' },
];

const InventoryCostsPage: React.FC = () => {
  const [period, setPeriod] = useState<Period>('month');
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery<InventoryCostsSummary, Error>({
    queryKey: ['inventory-costs', period],
    queryFn: () => inventoryCostsApi.getSummary(period),
  });

  const vehicleOptions =
    data?.vehicles?.map((v) => ({
      id: v.id,
      label: `${v.year ?? ''} ${v.make ?? ''} ${v.model ?? ''} · ${v.vin ?? ''}`.trim(),
    })) ?? [];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Inventory Costs</h1>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            className="rounded-md border-gray-300 text-sm shadow-sm"
            aria-label="Period filter"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setModalOpen(true)}
            disabled={!data?.vehicles?.length}
            className="btn-primary text-sm inline-flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" /> Add Expense
          </button>
        </div>
      </div>

      {isLoading && <p className="text-gray-500">Loading...</p>}
      {error && (
        <p className="text-red-600">Failed to load inventory costs: {(error as Error).message}</p>
      )}
      {data && (
        <>
          <KpiCards summary={data} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CategoryBreakdownPie
              summary={data}
              periodLabel={PERIODS.find((p) => p.value === period)?.label ?? period}
            />
            <MonthlySpendBar summary={data} />
          </div>
          <VehicleCostTable vehicles={data.vehicles} />
        </>
      )}

      <AddExpenseModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        vehicle={null}
        vehicleOptions={vehicleOptions}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
};

export default InventoryCostsPage;
